import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { UpdateTestModal } from '../components/UpdateTestModal';
import { BulkUpdateTestModal } from '../components/BulkUpdateTestModal';
import api from '../lib/api';
import { Download, Copy, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

type Tab = 'queue' | 'dashboard' | 'idle';

interface QueueItem {
  id: string;
  creative_id: string;
  creative_name: string;
  assigned_date: string;
  days_untested: number;
  status: string;
  editor_name: string | null;
  buyer_name: string | null;
  tested_date: string | null;
  platform: string | null;
  roi: number | null;
  ctr: number | null;
  hook_rate: number | null;
  hold_rate: number | null;
  feedback: string | null;
  untested_reason: string | null;
  file_request_id: string | null;
  file_request_title: string | null;
  file_request_created_at: string | null;
}

interface KpiStats {
  total_tested: number;
  total_untested: number;
  total_scheduled: number;
  avg_roi: number | null;
  avg_ctr: number | null;
  by_platform: { platform: string; count: number }[];
  escalations_7d_pending: number;
  escalations_14d_pending: number;
}

interface DashboardCreative {
  id: string;
  creative_name: string;
  editor_name: string;
  buyer_name: string | null;
  status: string;
  days_since_creation: number;
  qc_passed_at: string | null;
  platform: string;
  roi: number | null;
  ctr: number | null;
  untested_reason: string | null;
  tested_date: string | null;
}

interface IdleCreative {
  id: string;
  creative_id: string;
  creative_name: string;
  editor_name: string;
  roi: number | null;
  last_used: string | null;
}

type FilterState = { searchCreative: string; searchBuyer: string; dateFrom: string; dateTo: string };

const ADMIN_ROLES = ['admin', 'ceo', 'head_media_buying', 'creative_head', 'assistant_team_lead'];

function downloadCsv(rows: Record<string, unknown>[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]).join(',');
  const body = rows.map(r => Object.values(r).map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([headers + '\n' + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function Pagination({ page, totalPages, total, onPage }: { page: number; totalPages: number; total: number; onPage: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-1 pt-3">
      <span className="text-xs text-muted-foreground">{total} total</span>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          className="px-2.5 py-1 text-xs border border-border rounded disabled:opacity-40 hover:bg-muted transition-colors"
        >
          Prev
        </button>
        <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
        <button
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          className="px-2.5 py-1 text-xs border border-border rounded disabled:opacity-40 hover:bg-muted transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}

const STATUS_LABELS: Record<string, string> = {
  tested: 'Launched',
  scheduled: 'Scheduled',
  untested: 'Not Launched',
};

function StatusPill({ status }: { status: string }) {
  const cls =
    status === 'tested' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
    status === 'scheduled' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400' :
    'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>
      {STATUS_LABELS[status] ?? status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export function TestingTrackerPage() {
  const { user } = useAuth();
  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const allRoles = [user?.role, ...additionalRoles].filter(Boolean) as string[];
  const isAdminish = allRoles.some(r => ADMIN_ROLES.includes(r));

  const [activeTab, setActiveTab] = useState<Tab>('queue');
  const [loading, setLoading] = useState(false);

  // Queue tab
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [queuePage, setQueuePage] = useState(1);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queueTotalPages, setQueueTotalPages] = useState(1);
  const [queueStatus, setQueueStatus] = useState<'untested' | 'tested' | 'scheduled' | 'all'>('untested');

  // Filters
  const emptyFilters: FilterState = { searchCreative: '', searchBuyer: '', dateFrom: '', dateTo: '' };
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [pendingFilters, setPendingFilters] = useState<FilterState>(emptyFilters);

  // Selection & groups
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['__none__']));
  const [bulkModal, setBulkModal] = useState(false);

  // Dashboard tab
  const [kpi, setKpi] = useState<KpiStats | null>(null);
  const [dashboardCreatives, setDashboardCreatives] = useState<DashboardCreative[]>([]);
  const [dashboardPage, setDashboardPage] = useState(1);
  const [dashboardTotal, setDashboardTotal] = useState(0);
  const [dashboardTotalPages, setDashboardTotalPages] = useState(1);
  const [reportMonth, setReportMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [reportYear, setReportYear] = useState(String(new Date().getFullYear()));
  const [reportLoading, setReportLoading] = useState(false);

  // Idle tab
  const [idleCreatives, setIdleCreatives] = useState<IdleCreative[]>([]);
  const [idlePage, setIdlePage] = useState(1);
  const [idleTotal, setIdleTotal] = useState(0);
  const [idleTotalPages, setIdleTotalPages] = useState(1);

  // Modal
  const [updateModal, setUpdateModal] = useState<{ testId: string; status?: string } | null>(null);

  const fetchQueue = useCallback(async (page = queuePage, status = queueStatus, activeFilters = filters) => {
    setLoading(true);
    try {
      const res = await api.get('/creative-tests/buyer-queue', {
        params: { page, limit: 50, status, ...activeFilters },
      });
      setQueue(res.data?.data || []);
      setQueueTotal(res.data?.total || 0);
      setQueueTotalPages(res.data?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch buyer queue:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuePage]);

  const fetchDashboard = useCallback(async (page = dashboardPage) => {
    setLoading(true);
    try {
      const res = await api.get('/creative-tests/admin-dashboard', { params: { page, limit: 25 } });
      const d = res.data?.data || {};
      setKpi(d.stats || null);
      setDashboardCreatives(d.creatives || []);
      setDashboardTotal(d.total || 0);
      setDashboardTotalPages(d.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch admin dashboard:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardPage]);

  const fetchIdle = useCallback(async (page = idlePage) => {
    setLoading(true);
    try {
      const res = await api.get('/creative-tests/idle', { params: { page, limit: 25 } });
      setIdleCreatives(res.data?.data || []);
      setIdleTotal(res.data?.total || 0);
      setIdleTotalPages(res.data?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch idle creatives:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idlePage]);

  useEffect(() => {
    if (activeTab === 'queue') { setQueuePage(1); fetchQueue(1); }
    else if (activeTab === 'dashboard' && isAdminish) { setDashboardPage(1); fetchDashboard(1); }
    else if (activeTab === 'idle' && isAdminish) { setIdlePage(1); fetchIdle(1); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => { fetchQueue(queuePage, queueStatus, filters); }, [queuePage, queueStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdminish) fetchDashboard(dashboardPage); }, [dashboardPage]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdminish) fetchIdle(idlePage); }, [idlePage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group queue items by file_request_id
  const groupedQueue = useMemo(() => {
    const groups: Map<string, { id: string | null; title: string; items: QueueItem[] }> = new Map();
    queue.forEach(item => {
      const key = item.file_request_id || '__none__';
      if (!groups.has(key)) {
        groups.set(key, {
          id: item.file_request_id,
          title: item.file_request_title || 'Direct / Launch Request',
          items: [],
        });
      }
      groups.get(key)!.items.push(item);
    });
    return Array.from(groups.values());
  }, [queue]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleGroupSelection = (items: QueueItem[]) => {
    const ids = items.map(i => i.id);
    const allSelected = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const toggleItemSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Bulk update modal data
  const launchedItems = useMemo(
    () => queue.filter(item => selectedIds.has(item.id)),
    [queue, selectedIds]
  );

  const unlaunchedItems = useMemo(() => {
    // Find all groups that have at least one selected item
    const selectedGroupKeys = new Set<string>();
    queue.forEach(item => {
      if (selectedIds.has(item.id)) {
        selectedGroupKeys.add(item.file_request_id || '__none__');
      }
    });
    // Return items in those groups that are NOT selected
    return queue.filter(item => {
      const key = item.file_request_id || '__none__';
      return selectedGroupKeys.has(key) && !selectedIds.has(item.id);
    });
  }, [queue, selectedIds]);

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    setQueuePage(1);
    fetchQueue(1, queueStatus, pendingFilters);
  };

  const handleClearFilters = () => {
    setPendingFilters(emptyFilters);
    setFilters(emptyFilters);
    setQueuePage(1);
    fetchQueue(1, queueStatus, emptyFilters);
  };

  const handleDownloadReport = async () => {
    setReportLoading(true);
    try {
      const res = await api.get('/creative-tests/monthly-report', {
        params: { month: reportMonth, year: reportYear },
      });
      const rows = res.data?.data || [];
      if (!rows.length) { alert('No data for selected period'); return; }
      downloadCsv(rows, `testing-report-${reportYear}-${reportMonth}.csv`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to download report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleCopyLink = (creativeId: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/media/${creativeId}`);
  };

  const tabs: { key: Tab; label: string; adminOnly: boolean }[] = [
    { key: 'queue', label: 'My Queue', adminOnly: false },
    { key: 'dashboard', label: 'Admin Dashboard', adminOnly: true },
    { key: 'idle', label: 'Idle Creatives', adminOnly: true },
  ];

  // Columns count for colspan
  const queueColCount = queueStatus === 'tested' ? 9 : 7;

  return (
    <DashboardLayout title="Testing Tracker">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-border mb-6">
          {tabs
            .filter(t => !t.adminOnly || isAdminish)
            .map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === t.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
              </button>
            ))}
        </div>

        {/* MY QUEUE TAB */}
        {activeTab === 'queue' && (
          <div>
            {/* Filter bar */}
            <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-muted/30 border border-border rounded-lg">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Creative Name</label>
                <input
                  type="text"
                  placeholder="Search creative..."
                  value={pendingFilters.searchCreative}
                  onChange={e => setPendingFilters(p => ({ ...p, searchCreative: e.target.value }))}
                  className="px-2.5 py-1.5 text-sm border border-border rounded bg-background text-foreground outline-none focus:border-primary w-44"
                />
              </div>
              {isAdminish && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">Buyer Name</label>
                  <input
                    type="text"
                    placeholder="Search buyer..."
                    value={pendingFilters.searchBuyer}
                    onChange={e => setPendingFilters(p => ({ ...p, searchBuyer: e.target.value }))}
                    className="px-2.5 py-1.5 text-sm border border-border rounded bg-background text-foreground outline-none focus:border-primary w-44"
                  />
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">From</label>
                <input
                  type="date"
                  value={pendingFilters.dateFrom}
                  onChange={e => setPendingFilters(p => ({ ...p, dateFrom: e.target.value }))}
                  className="px-2.5 py-1.5 text-sm border border-border rounded bg-background text-foreground outline-none focus:border-primary"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">To</label>
                <input
                  type="date"
                  value={pendingFilters.dateTo}
                  onChange={e => setPendingFilters(p => ({ ...p, dateTo: e.target.value }))}
                  className="px-2.5 py-1.5 text-sm border border-border rounded bg-background text-foreground outline-none focus:border-primary"
                />
              </div>
              <Button size="sm" onClick={handleApplyFilters}>Apply</Button>
              <Button size="sm" variant="outline" onClick={handleClearFilters}>Clear</Button>
            </div>

            {/* Status filter + refresh row */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1">
                {([
                  { key: 'untested', label: 'Not Launched' },
                  { key: 'scheduled', label: 'Scheduled' },
                  { key: 'tested', label: 'Launched' },
                  { key: 'all', label: 'All' },
                ] as const).map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => { setQueueStatus(key); setQueuePage(1); setSelectedIds(new Set()); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      queueStatus === key
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => fetchQueue(queuePage, queueStatus, filters)}>
                <RefreshCw size={13} className="mr-1.5" /> Refresh
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-muted-foreground text-sm">Loading...</div>
            ) : queue.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                {queueStatus === 'tested' ? 'No launched creatives yet' :
                 queueStatus === 'scheduled' ? 'No scheduled creatives' :
                 'No creatives in your queue'}
              </div>
            ) : (
              <>
                <div className="border border-border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/60">
                      <tr>
                        <th className="w-8 p-3"></th>
                        <th className="text-left p-3 font-semibold text-muted-foreground">Creative Name</th>
                        <th className="text-left p-3 font-semibold text-muted-foreground">Editor</th>
                        <th className="text-left p-3 font-semibold text-muted-foreground">Buyer</th>
                        <th className="text-left p-3 font-semibold text-muted-foreground">Queue Entry Date</th>
                        {queueStatus === 'tested' ? (
                          <>
                            <th className="text-left p-3 font-semibold text-muted-foreground">Launched On</th>
                            <th className="text-left p-3 font-semibold text-muted-foreground">Platform</th>
                            <th className="text-left p-3 font-semibold text-muted-foreground">ROI</th>
                            <th className="text-left p-3 font-semibold text-muted-foreground">CTR</th>
                            <th className="text-left p-3 font-semibold text-muted-foreground">Hook Rate</th>
                            <th className="text-left p-3 font-semibold text-muted-foreground">Feedback</th>
                          </>
                        ) : (
                          <>
                            <th className="text-left p-3 font-semibold text-muted-foreground">Days Waiting</th>
                            <th className="text-left p-3 font-semibold text-muted-foreground">Reason</th>
                          </>
                        )}
                        <th className="text-left p-3 font-semibold text-muted-foreground">Status</th>
                        <th className="text-left p-3 font-semibold text-muted-foreground">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedQueue.map(group => {
                        const groupKey = group.id || '__none__';
                        const isExpanded = expandedGroups.has(groupKey);
                        const groupItemIds = group.items.map(i => i.id);
                        const allGroupSelected = groupItemIds.length > 0 && groupItemIds.every(id => selectedIds.has(id));
                        const someGroupSelected = groupItemIds.some(id => selectedIds.has(id));
                        const totalCols = queueStatus === 'tested' ? 13 : 9;

                        return (
                          <React.Fragment key={groupKey}>
                            {/* Group header row */}
                            <tr className="bg-muted/40 border-t border-border">
                              <td className="p-2 pl-3">
                                <input
                                  type="checkbox"
                                  checked={allGroupSelected}
                                  ref={el => { if (el) el.indeterminate = someGroupSelected && !allGroupSelected; }}
                                  onChange={() => toggleGroupSelection(group.items)}
                                  className="accent-primary cursor-pointer"
                                />
                              </td>
                              <td colSpan={totalCols - 1} className="p-2">
                                <button
                                  onClick={() => toggleGroup(groupKey)}
                                  className="flex items-center gap-2 text-sm font-semibold hover:text-primary transition-colors"
                                >
                                  {isExpanded
                                    ? <ChevronDown size={14} className="shrink-0" />
                                    : <ChevronRight size={14} className="shrink-0" />}
                                  <span>{group.title}</span>
                                  <span className="text-xs font-normal text-muted-foreground">
                                    ({group.items.length} creative{group.items.length !== 1 ? 's' : ''})
                                  </span>
                                  {queueStatus === 'untested' && (
                                    <button
                                      onClick={e => { e.stopPropagation(); toggleGroupSelection(group.items); }}
                                      className="ml-2 text-xs text-primary underline font-normal"
                                    >
                                      Select All
                                    </button>
                                  )}
                                </button>
                              </td>
                            </tr>

                            {/* Item rows */}
                            {isExpanded && group.items.map((item, i) => (
                              <tr key={item.id} className={`border-t border-border ${i % 2 === 1 ? 'bg-muted/20' : ''}`}>
                                <td className="p-3 pl-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.has(item.id)}
                                    onChange={() => toggleItemSelection(item.id)}
                                    className="accent-primary cursor-pointer"
                                  />
                                </td>
                                <td className="p-3 font-medium truncate max-w-[180px]">{item.creative_name}</td>
                                <td className="p-3 text-muted-foreground whitespace-nowrap">{item.editor_name || '—'}</td>
                                <td className="p-3 text-muted-foreground whitespace-nowrap">{item.buyer_name || '—'}</td>
                                <td className="p-3 text-muted-foreground whitespace-nowrap">
                                  {item.assigned_date ? new Date(item.assigned_date).toLocaleDateString() : '—'}
                                </td>
                                {queueStatus === 'tested' ? (
                                  <>
                                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                                      {item.tested_date ? new Date(item.tested_date).toLocaleDateString() : '—'}
                                    </td>
                                    <td className="p-3 text-muted-foreground">{item.platform || '—'}</td>
                                    <td className="p-3 text-muted-foreground">{item.roi != null ? item.roi.toFixed(2) : '—'}</td>
                                    <td className="p-3 text-muted-foreground">{item.ctr != null ? `${item.ctr.toFixed(2)}%` : '—'}</td>
                                    <td className="p-3 text-muted-foreground">{item.hook_rate != null ? `${item.hook_rate.toFixed(2)}%` : '—'}</td>
                                    <td className="p-3 text-muted-foreground max-w-[160px] truncate" title={item.feedback || ''}>{item.feedback || '—'}</td>
                                  </>
                                ) : (
                                  <>
                                    <td className="p-3 text-muted-foreground">{item.days_untested ?? '—'}</td>
                                    <td className="p-3 text-muted-foreground text-xs">
                                      {item.untested_reason ? item.untested_reason.replace(/_/g, ' ') : '—'}
                                    </td>
                                  </>
                                )}
                                <td className="p-3"><StatusPill status={item.status} /></td>
                                <td className="p-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setUpdateModal({ testId: item.id, status: item.status })}
                                  >
                                    {item.status === 'tested' ? 'Edit' : 'Update'}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination page={queuePage} totalPages={queueTotalPages} total={queueTotal} onPage={p => { setQueuePage(p); setSelectedIds(new Set()); }} />
              </>
            )}

            {/* Sticky bulk action bar */}
            {selectedIds.size > 0 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-card border border-border shadow-xl rounded-xl px-5 py-3 flex items-center gap-4">
                <span className="text-sm font-medium">{selectedIds.size} selected as Launched</span>
                <Button size="sm" onClick={() => setBulkModal(true)}>
                  Bulk Update
                </Button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}

        {/* ADMIN DASHBOARD TAB */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {loading && !kpi ? (
              <div className="text-center py-12 text-muted-foreground text-sm">Loading dashboard...</div>
            ) : (
              <>
                {/* KPI Strip */}
                {kpi && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      { label: 'Total Tested', value: kpi.total_tested ?? 0 },
                      { label: 'Total Untested', value: kpi.total_untested ?? 0 },
                      { label: 'Scheduled', value: kpi.total_scheduled ?? 0 },
                      { label: 'Avg ROI', value: kpi.avg_roi != null ? Number(kpi.avg_roi).toFixed(2) : '—' },
                      { label: 'Avg CTR', value: kpi.avg_ctr != null ? `${Number(kpi.avg_ctr).toFixed(2)}%` : '—' },
                      { label: '7d Escalations', value: kpi.escalations_7d_pending ?? 0 },
                    ].map(stat => (
                      <div key={stat.label} className="bg-card border border-border rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                        <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Monthly Report Download */}
                <div className="flex items-center gap-3 bg-muted/30 border border-border rounded-lg p-4">
                  <span className="text-sm font-medium">Download Monthly Report:</span>
                  <select
                    value={reportMonth}
                    onChange={e => setReportMonth(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                  >
                    {Array.from({ length: 12 }, (_, i) => {
                      const m = String(i + 1).padStart(2, '0');
                      const label = new Date(2000, i).toLocaleString('default', { month: 'long' });
                      return <option key={m} value={m}>{label}</option>;
                    })}
                  </select>
                  <input
                    type="number"
                    value={reportYear}
                    onChange={e => setReportYear(e.target.value)}
                    min="2020"
                    max="2099"
                    className="w-24 px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                  />
                  <Button size="sm" onClick={handleDownloadReport} disabled={reportLoading}>
                    <Download size={13} className="mr-1.5" />
                    {reportLoading ? 'Downloading...' : 'Download CSV'}
                  </Button>
                </div>

                {/* Creatives Table */}
                {dashboardCreatives.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground text-sm">No untested creatives</div>
                ) : (
                  <><div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/60">
                        <tr>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Creative Name</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Editor</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Buyer</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Status</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Queue Entry Date</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Platform</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">ROI</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">CTR</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Untested Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardCreatives.map((c, i) => (
                          <tr key={c.id} className={`border-t border-border ${i % 2 === 1 ? 'bg-muted/20' : ''}`}>
                            <td className="p-3 font-medium truncate max-w-xs">{c.creative_name}</td>
                            <td className="p-3 text-muted-foreground">{c.editor_name || '—'}</td>
                            <td className="p-3 text-muted-foreground">{c.buyer_name || '—'}</td>
                            <td className="p-3"><StatusPill status={c.status} /></td>
                            <td className="p-3 text-muted-foreground">{c.days_since_creation ?? '—'}</td>
                            <td className="p-3 text-muted-foreground">{c.platform || '—'}</td>
                            <td className="p-3 text-muted-foreground">{c.roi != null ? c.roi.toFixed(2) : '—'}</td>
                            <td className="p-3 text-muted-foreground">{c.ctr != null ? `${c.ctr.toFixed(2)}%` : '—'}</td>
                            <td className="p-3 text-muted-foreground">{c.untested_reason || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination page={dashboardPage} totalPages={dashboardTotalPages} total={dashboardTotal} onPage={setDashboardPage} />
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* IDLE CREATIVES TAB */}
        {activeTab === 'idle' && (
          <div>
            <p className="text-sm text-muted-foreground mb-4">High-ROI creatives not used in the last 30 days</p>
            {loading ? (
              <div className="text-center py-12 text-muted-foreground text-sm">Loading idle creatives...</div>
            ) : idleCreatives.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">No idle creatives found</div>
            ) : (
              <>
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60">
                    <tr>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Creative Name</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Editor</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">ROI</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Last Used</th>
                      <th className="text-left p-3 font-semibold text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {idleCreatives.map((c, i) => (
                      <tr key={c.id} className={`border-t border-border ${i % 2 === 1 ? 'bg-muted/20' : ''}`}>
                        <td className="p-3 font-medium truncate max-w-xs">{c.creative_name}</td>
                        <td className="p-3 text-muted-foreground">{c.editor_name || '—'}</td>
                        <td className="p-3 text-muted-foreground">{c.roi != null ? c.roi.toFixed(2) : '—'}</td>
                        <td className="p-3 text-muted-foreground">
                          {c.last_used ? new Date(c.last_used).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => handleCopyLink(c.creative_id || c.id)}
                            className="flex items-center gap-1.5 px-3 py-1 text-xs border border-border rounded hover:bg-muted transition-colors"
                          >
                            <Copy size={12} /> Copy Link
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={idlePage} totalPages={idleTotalPages} total={idleTotal} onPage={setIdlePage} />
              </>
            )}
          </div>
        )}
      </div>

      {/* Update Test Modal */}
      {updateModal && (
        <UpdateTestModal
          testId={updateModal.testId}
          currentStatus={updateModal.status}
          onClose={() => setUpdateModal(null)}
          onSaved={() => {
            setUpdateModal(null);
            if (activeTab === 'queue') fetchQueue(queuePage, queueStatus, filters);
            else if (activeTab === 'dashboard') fetchDashboard(dashboardPage);
          }}
        />
      )}

      {/* Bulk Update Modal */}
      {bulkModal && (
        <BulkUpdateTestModal
          launchedItems={launchedItems}
          unlaunchedItems={unlaunchedItems}
          onClose={() => setBulkModal(false)}
          onSaved={() => {
            setBulkModal(false);
            setSelectedIds(new Set());
            fetchQueue(queuePage, queueStatus, filters);
          }}
        />
      )}
    </DashboardLayout>
  );
}
